import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
  Button,
  Box,
  InputAdornment,
} from "@material-ui/core";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import { Cached } from "@material-ui/icons";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import moment from "moment";
import PaginationInput from "../../../models/pagination/PaginationInput";
import MessagesTimeline from "./MessagesTimeline";
import { fetchMessagesAPI } from "./messagesAPI";
import { useStyles } from './AllBatchUpdatesStyles';

const AllBatchUpdates = () => {
  const classes = useStyles();
  const history = useHistory();
  const [viewBy1, setViewBy1] = useState("Timestamp");
  const [viewBy2, setViewBy2] = useState("Last 3 Days");
  const [searchValue, setSearchValue] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [allBatchUpdatesData, setAllBatchUpdatesData] = useState([]);
  const viewType = "card"; // Card view is approved, no toggle needed

  // Get options for second dropdown based on first dropdown selection
  const getSecondDropdownOptions = (viewByType) => {
    switch (viewByType) {
      case "Timestamp":
        return [
          { value: "Last 3 Days", label: "Last 3 Days" },
          { value: "Last 24 Hours", label: "Last 24 Hours" },
          { value: "Last 1 Week", label: "Last 1 Week" },
          { value: "Last 2 Weeks", label: "Last 2 Weeks" },
          { value: "Last 1 Month", label: "Last 1 Month" },
        ];
      case "Message Type":
        return [
          { value: "Material", label: "Material" },
          { value: "CPP", label: "CPP" },
          { value: "Information Testing", label: "Information Testing" },
          { value: "In-Process Testing", label: "In-Process Testing" },
          { value: "Deviations", label: "Deviations" },
        ];
      case "Batch Number":
        return [
          { value: "All", label: "All" },
          { value: "400682", label: "400682" },
          { value: "400683", label: "400683" },
          { value: "400684", label: "400684" },
        ];
      case "Unit Procedure":
        return [
          { value: "All", label: "All" },
          { value: "B", label: "B" },
          { value: "F", label: "F" },
          { value: "C", label: "C" },
          { value: "D", label: "D" },
          { value: "H", label: "H" },
        ];
      default:
        return [];
    }
  };

  // Get default value when first dropdown changes
  const getDefaultValue = (viewByType) => {
    switch (viewByType) {
      case "Timestamp":
        return "Last 3 Days";
      case "Message Type":
        return "Material";
      case "Batch Number":
        return "All";
      case "Unit Procedure":
        return "All";
      default:
        return "";
    }
  };

  // Fetch messages when filters or pagination change
  useEffect(() => {
    const loadMessages = async () => {
      try {
        const response = await fetchMessagesAPI({
          // For now using static values; backend can use these later
          projectId: "AR - LDD",
          batchId: "all",
          operation: "all",
          viewBy: viewBy1,
          viewByValue: viewBy2,
          pageSize: rowsPerPage,
          pageNumber: currentPage,
        });

        if (response.status && response.data && response.data.batches) {
          setAllBatchUpdatesData(response.data.batches);
          setTotalPages(response.data.totalPages || 0);
        }
      } catch (error) {
        console.error("Error fetching messages:", error);
      }
    };

    loadMessages();
  }, [viewBy1, viewBy2, currentPage, rowsPerPage]);

  const paginatedData = allBatchUpdatesData;

  const handleBack = () => {
    history.goBack();
  };

  const handleRefresh = () => {
    console.log("Refreshing data...");
  };

  const handleClearFilters = () => {
    setSearchValue("");
    setViewBy1("Timestamp");
    setViewBy2("Last 3 Days");
  };

  const handleSearch = (e) => {
    if (e) {
      e.preventDefault();
    }
    console.log("Searching...", searchValue);
  };

  const handleClearSearch = () => {
    setSearchValue("");
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <div className={classes.backButton} onClick={handleBack}>
            <ChevronLeftIcon />
            <Typography variant="body1">Back</Typography>
          </div>
          <Typography className={classes.title}>All Batch Updates</Typography>
        </div>
      </div>

      <div className={classes.productInfo}>
        <div className={classes.productInfoItem}>
          <Typography className={classes.productInfoLabel}>
            Product Name
          </Typography>
          <Typography className={classes.productInfoValue}>
            Actazosin Step 3
          </Typography>
        </div>
        <div className={classes.productInfoItem}>
          <Typography className={classes.productInfoLabel}>
            CMO Name
          </Typography>
          <Typography className={classes.productInfoValue}>
            Sai Life Sciences
          </Typography>
        </div>
      </div>

      <div className={classes.filterSection}>
        <div className={classes.filterLeft}>
          <div className={classes.viewByGroup}>
            <Typography className={classes.viewByLabel}>View By:</Typography>
            <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
              <Select value={viewBy1} onChange={(e) => {
                const newType = e.target.value;
                setViewBy1(newType);
                setViewBy2(getDefaultValue(newType));
              }}>
                <MenuItem value="Timestamp">Timestamp</MenuItem>
                <MenuItem value="Message Type">Message Type</MenuItem>
                <MenuItem value="Batch Number">Batch Number</MenuItem>
                <MenuItem value="Unit Procedure">Unit Procedure</MenuItem>
              </Select>
            </FormControl>
          </div>
          <div style={{ marginTop: "28px" }}>
            <FormControl variant="outlined" size="small" style={{ minWidth: 170 }}>
              <Select value={viewBy2} onChange={(e) => setViewBy2(e.target.value)}>
                {getSecondDropdownOptions(viewBy1).map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <Box style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "28px" }}>
            <TextField
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder=""
              className={classes.searchInput}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "#666", marginRight: "8px" }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              className={classes.searchButton}
            >
              Search
            </Button>
            <Typography
              className={classes.clearFilters}
              onClick={handleClearFilters}
            >
              Clear all filters
            </Typography>
          </Box>
        </div>
        <div className={classes.filterRight}>
          <div className={classes.refreshContainer}>
            <Typography variant="caption" className={classes.padR20}>
              {`Last synced @ ${moment().format("HH:mm:ss; DD MMMM, YYYY")}`}
            </Typography>
            <Typography
              variant="caption"
              color="primary"
              className={classes.refreshText}
              onClick={handleRefresh}
            >
              Refresh
              <Cached color="primary" style={{ fontSize: 15 }} />
            </Typography>
          </div>
          <div className={classes.perPageContainer}>
            <Typography variant="body2" className={classes.hgText}>
              PER PAGE
            </Typography>
            <FormControl className={classes.formPerPage} variant="outlined">
              <Select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(e.target.value);
                  setCurrentPage(1);
                }}
                labelId="perpage-filter"
                id="perpage-filter-id"
                style={{ outline: 'none' }}
                onFocus={(e) => (e.target.style.outline = 'none')}
                classes={{
                  outlined: classes.outlined,
                }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
          </div>
        </div>
      </div>

      <div style={{ marginTop: "20px" }}>
        <MessagesTimeline 
          batches={paginatedData} 
          viewType={viewType}
        />
      </div>

      <div className={classes.paginationContainer}>
        <PaginationInput
          totalPages={totalPages}
          handlePageChange={setCurrentPage}
          page={currentPage}
        />
      </div>
    </div>
  );
};

export default AllBatchUpdates;

